import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const loadingTexts = [
  "Initializing experience...",
  "Loading creative assets...",
  "Setting up smooth interactions...",
  "Finalizing portfolio...",
  "Welcome to my world."
];

interface LoadingPageProps {
  onComplete?: () => void;
}

export const LoadingPage: React.FC<LoadingPageProps> = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [textIndex, setTextIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 150);

    const textInterval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % loadingTexts.length);
    }, 2000);

    return () => {
      clearInterval(interval);
      clearInterval(textInterval);
    };
  }, []);

  // When loading reaches 100%, trigger completion callback smoothly
  useEffect(() => {
    if (progress >= 100) {
      const timer = setTimeout(() => {
        onComplete?.();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [progress, onComplete]);

  return (
    <motion.div 
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.02, transition: { duration: 0.5, ease: "easeInOut" } }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-50 dark:bg-brand-dark-bg transition-colors duration-500 overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-brand-purple/30 blur-[100px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-brand-light/30 blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 flex flex-col items-center">
        {/* Logo Animation */}
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-10 relative flex items-center justify-center"
        >
          {/* Ambient aura glow behind the logo */}
          <div className="absolute w-56 h-56 sm:w-64 sm:h-64 rounded-full bg-brand-purple/25 dark:bg-brand-light/20 blur-3xl animate-pulse pointer-events-none" />

          {/* Standalone large logo with smooth floating animation */}
          <motion.img
            src="/assets/logo.png"
            alt="Nugi Logo"
            className="w-36 h-36 sm:w-44 sm:h-44 md:w-52 md:h-52 object-contain select-none"
            animate={{
              y: [-6, 6, -6],
            }}
            transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              filter: 'drop-shadow(0 12px 24px rgba(107, 33, 168, 0.35))',
            }}
          />
        </motion.div>

        {/* Progress bar container */}
        <div className="w-64 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden mb-6 backdrop-blur-sm border border-slate-300/20 dark:border-slate-700/20">
          <motion.div
            className="h-full bg-gradient-to-r from-brand-purple via-brand-light to-brand-purple"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(progress, 100)}%` }}
            transition={{ ease: "easeInOut" }}
            style={{ backgroundSize: '200% 100%' }}
          />
        </div>

        {/* Dynamic loading text */}
        <div className="h-8 flex flex-col items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={textIndex}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="text-brand-navy/60 dark:text-brand-dark-text-secondary font-medium tracking-wide"
            >
              {loadingTexts[textIndex]}
              <span className="loading-dots"></span>
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Percentage display */}
        <motion.div 
          className="mt-4 text-xs font-mono text-brand-purple dark:text-brand-light opacity-50"
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {Math.min(Math.round(progress), 100)}%
        </motion.div>
      </div>

      {/* Decorative lines */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-purple/20 to-transparent blur-sm" />
    </motion.div>
  );
};

export default LoadingPage;
